// Audio file management and storage system

import type { AudioManager } from "../interfaces/audio-recording.js";
import type { IndexedDBWrapper } from "../storage/indexeddb-wrapper.js";
import type { AudioFile, StorageInfo } from "../types/core.js";

export class LocalAudioManager implements AudioManager {
	private dbWrapper: IndexedDBWrapper;
	private readonly MAX_STORAGE_MB = 2000; // Increased to 2GB
	private readonly CLEANUP_THRESHOLD = 0.8; // Warn when 80% full
	private initializationPromise: Promise<void> | null = null;

	constructor(dbWrapper: IndexedDBWrapper) {
		this.dbWrapper = dbWrapper;
		this.initializationPromise = this.ensureInitialized();
	}

	private async ensureInitialized(): Promise<void> {
		try {
			// Check if database is already initialized
			if (this.dbWrapper.isInitialized()) {
				return;
			}

			// Initialize the database if not already done
			await this.dbWrapper.initialize();
		} catch (error) {
			console.warn(
				"Database initialization failed, using fallback mode:",
				error,
			);
			// In test environment or when IndexedDB is not available,
			// we'll continue with mock functionality
		}
	}

	private async waitForInitialization(): Promise<void> {
		if (this.initializationPromise) {
			await this.initializationPromise;
		}
	}

	async saveAudioFile(audio: AudioFile, sessionId: string): Promise<string> {
		try {
			// Ensure database is initialized
			await this.waitForInitialization();

			// Set the session ID
			const audioWithSession = { ...audio, sessionId };

			// Check storage quota before saving
			const storageInfo = await this.getStorageUsage();
			const audioSizeMB = audio.size / (1024 * 1024);

			if (storageInfo.used + audioSizeMB > this.MAX_STORAGE_MB) {
				console.warn(
					`Storage usage (${storageInfo.used.toFixed(1)}MB) exceeds soft limit (${this.MAX_STORAGE_MB}MB). Proceeding with save, but cleanup is recommended.`,
				);
			}

			// Performance optimization: Save audio file (including its blob) in a single pass.
			// Since audioWithSession already contains the blob, dbWrapper.saveAudioFile (which performs a db.put)
			// persists both simultaneously. Calling a separate saveAudioBlob method afterward would incur
			// a redundant and expensive get+put cycle on the database.
			await this.dbWrapper.saveAudioFile(audioWithSession);

			return audio.id;
		} catch (error) {
			throw new Error(
				`Failed to save audio file: ${error instanceof Error ? error.message : "Unknown error"}`,
			);
		}
	}

	async getAudioFiles(userId: string): Promise<AudioFile[]> {
		try {
			// Ensure database is initialized
			await this.waitForInitialization();

			// Get all sessions for the user first
			const sessions = await this.dbWrapper.getSessionsByUser(userId);
			const sessionIds = sessions.map((session) => session.id);

			// Get audio files for all user sessions
			const allAudioFiles: AudioFile[] = [];
			for (const sessionId of sessionIds) {
				const sessionAudioFiles =
					await this.dbWrapper.getAudioFilesBySession(sessionId);
				allAudioFiles.push(...sessionAudioFiles);
			}

			// Sort by creation date (newest first)
			return allAudioFiles.sort(
				(a, b) => b.createdAt.getTime() - a.createdAt.getTime(),
			);
		} catch (error) {
			throw new Error(
				`Failed to retrieve audio files: ${error instanceof Error ? error.message : "Unknown error"}`,
			);
		}
	}

	async deleteAudioFile(fileId: string): Promise<boolean> {
		try {
			// Ensure database is initialized
			await this.waitForInitialization();

			// Delete audio file metadata (and its blob, since they're stored together)
			await this.dbWrapper.deleteAudioFile(fileId);

			return true;
		} catch (error) {
			console.error(`Failed to delete audio file ${fileId}:`, error);
			return false;
		}
	}

	async getStorageUsage(): Promise<StorageInfo> {
		try {
			const estimate = await this.dbWrapper.getStorageEstimate();
			const usageMB = estimate.usage / (1024 * 1024);
			const quotaMB = estimate.quota / (1024 * 1024);

			return {
				used: usageMB,
				available: quotaMB - usageMB,
				quota: quotaMB,
			};
		} catch (error) {
			console.error("Failed to get storage usage:", error);
			return {
				used: 0,
				available: this.MAX_STORAGE_MB,
				quota: this.MAX_STORAGE_MB,
			};
		}
	}

	// Additional utility methods

	async getAudioFilesBySession(sessionId: string): Promise<AudioFile[]> {
		try {
			// Ensure database is initialized
			await this.waitForInitialization();

			return await this.dbWrapper.getAudioFilesBySession(sessionId);
		} catch (error) {
			throw new Error(
				`Failed to get audio files for session: ${error instanceof Error ? error.message : "Unknown error"}`,
			);
		}
	}

	async getAudioBlob(fileId: string): Promise<Blob | null> {
		try {
			// Ensure database is initialized
			await this.waitForInitialization();

			const audioFile = await this.dbWrapper.getAudioFile(fileId);
			return audioFile?.blob || null;
		} catch (error) {
			console.error(`Failed to get audio blob for ${fileId}:`, error);
			return null;
		}
	}

	// Storage monitoring
	async isStorageQuotaExceeded(): Promise<boolean> {
		const storageInfo = await this.getStorageUsage();
		return storageInfo.used / storageInfo.quota > this.CLEANUP_THRESHOLD;
	}

	async getStorageStats(): Promise<{
		totalFiles: number;
		totalSizeMB: number;
		oldestFile: Date | null;
		newestFile: Date | null;
	}> {
		try {
			// Ensure database is initialized
			await this.waitForInitialization();

			const stats = await this.dbWrapper.getAudioStorageStats();

			return {
				totalFiles: stats.totalFiles,
				totalSizeMB: stats.totalSize / (1024 * 1024),
				oldestFile: stats.oldestFile,
				newestFile: stats.newestFile,
			};
		} catch (error) {
			console.error("Failed to get storage stats:", error);
			return {
				totalFiles: 0,
				totalSizeMB: 0,
				oldestFile: null,
				newestFile: null,
			};
		}
	}
}
